// Character select — rows per PROTOCOL char_select. Delete confirms by
// naming the character; the name input is never rebuilt so
// typing is never clobbered. Settings gear (audio-less web build: theme).
//
// Creation is the CLASSIC stoneheim gate scene, staged (client presentation
// only — the server stays the authority):
//   list  → "New character"
//   gates → arrival narration, "Approach as a lad / lass"
//   ask   → the hard-of-hearing Guide asks your name, "Tell him" sends
//           create_character {name, sex}; while he "squints and scratches"
//           the reply is in flight. A served char_select.error = "Eh? Come
//           again?" and another try.
//   verdict (served char_select.guide) → his line + the FINAL name — the
//           character already exists under it; Begin is a plain
//           enter_character. Input validation only MIRRORS the served rule.

import { armBtn, clear, el, kbtn, tooltip } from '../kit';
import { sendEvent } from '../net';
import * as vsj from '../vsj';
import { reducedMotion } from '../ui';
import { openSettings } from '../components/settings';

// mirrors the driver's requested-name rule (guide.ts validInput) — never edits
const NAME_OK = (n: string) => /^[A-Za-z']{3,16}$/.test(n) && !n.includes("''");
const NAME_RULE = "Names are 3-16 letters and ' (never two '' in a row).";

// the classic gate-scene copy (stoneheim authPage.js, verbatim)
const ARRIVAL =
  'The road ends at the gates of Stoneheim. Behind a desk buried in '
  + 'ledgers sits a white-bearded old man, stamping papers without '
  + 'looking up. The line shuffles forward. You are next.';
const GREETING =
  `"Next! New arrival, eh? Every soul that passes these gates goes in `
  + `my ledger. Name for the papers? Speak up — I'm hard of hearing."`;
const SCRATCHING = 'The old man squints and scratches at your papers…';
const MISHEARD = `"Eh? Come again?"`;

function agoText(ms: unknown): string {
  if (typeof ms !== 'number') return '';
  const secs = (Date.now() - ms) / 1000;
  if (secs < 60) return 'just now';
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

export function charSelectScreen(): { root: HTMLElement; onVsj(changed: Set<string>): void } {
  // which scene fills the card; the served guide verdict overrides them all
  let stage: 'list' | 'gates' | 'ask' = 'list';
  let sex: 'lad' | 'lass' = 'lad';
  let pending = false; // a create_character is in flight
  let lastSpeech: string | null = null; // verdict speech already typed out
  let dismissed: string | null = null; // a served verdict the player backed out of
  const gkey = (g: any) => `${g?.name}|${g?.speech}`;

  const body = el('div', { class: 'char-flow' });
  const error = el('div', { class: 'error-line' });
  const nameEdit = el('input', { class: 'k-input', placeholder: "your name (letters and ' only)", maxlength: '16' });

  // slow-reveal for narration and the Guide's lines (instant under reduced motion)
  let typeTimer: ReturnType<typeof setInterval> | null = null;
  const typewriter = (node: HTMLElement, text: string) => {
    if (typeTimer) { clearInterval(typeTimer); typeTimer = null; }
    if (reducedMotion()) { node.textContent = text; return; }
    node.textContent = '';
    let i = 0;
    typeTimer = setInterval(() => {
      if (!node.isConnected) { clearInterval(typeTimer!); typeTimer = null; return; }
      node.textContent = text.slice(0, ++i);
      if (i >= text.length) { clearInterval(typeTimer!); typeTimer = null; }
    }, 25);
  };

  const gear = el('button', { class: 'k-btn', text: '⚙', title: 'Sound and theme settings.' });
  gear.addEventListener('click', () => openSettings(false));

  const backLink = () =>
    el(
      'div',
      { style: 'text-align:center' },
      el('button', {
        class: 'k-btn compact',
        text: 'back',
        onclick: () => { stage = 'list'; pending = false; error.textContent = ''; rebuild(); },
      }),
    );

  const makeRow = (ch: any): HTMLElement => {
    const row = el(
      'div',
      { class: 'k-panel raised char-card' },
      el('div', { class: 'k-title', style: 'min-width:140px', text: String(ch.name ?? '?') }),
    );
    if (ch.last_entered) {
      const chip = el('span', { class: 'k-chip', text: 'last' });
      tooltip(chip, () => 'Your most recently played character.');
      row.append(chip);
    }
    const doing = el('div', {
      class: 'k-dim',
      style: 'flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap',
      text: String(ch.doing ?? ''),
    });
    row.append(
      // served `level` = THE character level (max of all skill/stat levels)
      el('div', { class: 'k-dim', text: `Lv ${ch.level ?? '?'}` }),
      doing,
      el('div', { class: 'k-faint k-small', text: agoText(ch.last_played) }),
      kbtn(ch.enter, { label: 'Enter' }),
      armBtn(ch.delete, 'Delete'),
    );
    return row;
  };

  // ---- list: the roster + "New character" ----
  const renderList = () => {
    const cs = vsj.at('char_select') ?? {};
    const chars = cs.characters ?? {};
    const names = Object.keys(chars).sort();
    const rows = el('div', { class: 'char-list' });
    for (const n of names) rows.append(makeRow(chars[n]));
    if (!names.length)
      rows.append(el('div', { class: 'k-dim', text: 'No characters yet — step up to the gates below.' }));

    const foot = cs.create
      ? el('button', {
          class: 'k-btn',
          text: 'New character',
          onclick: () => { error.textContent = ''; stage = 'gates'; rebuild(); },
        })
      : el('div', { class: 'k-faint k-small', text: `character limit reached (${cs.cap ?? '?'})` });

    clear(body).append(
      el(
        'div',
        { style: 'display:flex;align-items:center;gap:8px' },
        el('div', { class: 'k-title', style: 'flex:1', text: 'Characters' }),
        gear,
      ),
      rows,
      el('div', { class: 'k-faint k-small', text: `${names.length} / ${cs.cap ?? '?'} characters` }),
      foot,
      error,
      el(
        'div',
        { style: 'display:flex;justify-content:flex-end' },
        el('button', { class: 'k-btn', text: 'Log out', onclick: () => sendEvent('logout') }),
      ),
    );
  };

  // ---- gates: arrival narration, step up as a lad or a lass ----
  const renderGates = () => {
    const narr = el('div', { class: 'guide-narration' });
    const approach = (s: 'lad' | 'lass') => () => { sex = s; stage = 'ask'; rebuild(); };
    clear(body).append(
      el('div', { class: 'k-title', style: 'text-align:center', text: 'The Gates of Stoneheim' }),
      narr,
      el(
        'div',
        { class: 'guide-choice' },
        el('button', { class: 'k-btn', text: 'Approach as a lad', onclick: approach('lad') }),
        el('button', { class: 'k-btn', text: 'Approach as a lass', onclick: approach('lass') }),
      ),
      backLink(),
    );
    typewriter(narr, ARRIVAL);
  };

  // ---- ask: the Guide wants a name for the papers ----
  const askSpeech = el('div', { class: 'guide-speech' });
  const tellBtn = el('button', { class: 'k-btn', text: 'Tell him' });
  const onTell = () => {
    if (pending) return;
    // the driver owns the rule; this check only MIRRORS it for instant feedback
    const name = nameEdit.value.trim();
    if (!NAME_OK(name)) { error.textContent = NAME_RULE; return; }
    error.textContent = '';
    pending = true;
    dismissed = null; // a re-ask may serve an identical verdict — show it
    tellBtn.disabled = true;
    if (typeTimer) { clearInterval(typeTimer); typeTimer = null; }
    askSpeech.textContent = SCRATCHING;
    const btn = vsj.at('char_select.create');
    sendEvent(btn?._event_id ?? 'create_character', { name, sex });
  };
  tellBtn.addEventListener('click', onTell);
  nameEdit.addEventListener('keydown', (e) => e.key === 'Enter' && onTell());

  const renderAsk = () => {
    tellBtn.disabled = pending;
    clear(body).append(
      el('div', { class: 'k-title', style: 'text-align:center', text: 'The Guide' }),
      askSpeech,
      el('div', { style: 'display:flex;gap:6px' }, nameEdit, tellBtn),
      error,
      backLink(),
    );
    if (!pending) {
      typewriter(askSpeech, GREETING);
      nameEdit.focus();
    }
  };

  // ---- verdict: what he wrote on your papers is final ----
  const renderVerdict = (g: any) => {
    const speech = String(g.speech ?? '');
    const speechEl = el('div', { class: 'guide-speech' });
    clear(body).append(
      el('div', { class: 'k-title', style: 'text-align:center', text: 'The Guide' }),
      speechEl,
      el('div', { class: 'guide-name', text: String(g.name ?? '') }),
      kbtn(g.enter, { label: `Begin as ${String(g.name ?? '')}` }),
      // the name is final either way; back just returns to the roster,
      // where the new character already has a row
      el(
        'div',
        { style: 'text-align:center' },
        el('button', {
          class: 'k-btn compact',
          text: 'back',
          onclick: () => { dismissed = gkey(g); stage = 'list'; rebuild(); },
        }),
      ),
    );
    if (speech !== lastSpeech) typewriter(speechEl, `“${speech}”`);
    else speechEl.textContent = `“${speech}”`;
    lastSpeech = speech;
  };

  const rebuild = () => {
    const cs = vsj.at('char_select') ?? {};
    const g = cs.guide;
    if (g && gkey(g) !== dismissed) { pending = false; renderVerdict(g); return; }
    lastSpeech = null;
    if (pending && cs.error != null) {
      // the create came back refused: the Guide didn't catch that
      pending = false;
      stage = 'ask';
      renderAsk();
      typewriter(askSpeech, MISHEARD);
      error.textContent = String(cs.error);
      return;
    }
    if (stage === 'gates') renderGates();
    else if (stage === 'ask') renderAsk();
    else {
      renderList();
      if (cs.error != null) error.textContent = String(cs.error);
    }
  };

  const root = el(
    'div',
    { class: 'center-screen' },
    el('div', { class: 'k-panel auth-card', style: 'width:520px' }, body),
  );
  rebuild();

  return {
    root,
    onVsj(changed) {
      if (changed.has('char_select') || changed.has('*')) rebuild();
    },
  };
}
